import React from 'react';
import { ContextMenuItem, Grid, GridColumn, GridRow, Tooltip } from '@axiom/ui';
import type { ProfileStatus } from '@axiom/ui';
import { CandidatesConst } from '@axiom/const';

import {
  UserBenchTalentSort,
  UserBenchTalentSortKeys,
  UserBenchTalentTypes,
} from '../../api/user-talent-api';

// Named export to match filename
export const BenchViewGridUtil = {};

type ProfileStatusValue = ProfileStatus | readonly ProfileStatus[];

type ProfileStatusFilterItem = {
  label: string;
  value: ProfileStatusValue;
};

export const UserTalentFilterByProfileStatusList: ProfileStatusFilterItem[] = [
  {
    label: 'All',
    value: Object.values(CandidatesConst.ProfileStatuses),
  },
  {
    label: CandidatesConst.ProfileStatusLists.allCertifiedTalent.name,
    value: CandidatesConst.ProfileStatusLists.allCertifiedTalent.value,
  },
  ...[
    CandidatesConst.ProfileStatuses.Certifying,
    CandidatesConst.ProfileStatuses.InDiligence,
    CandidatesConst.ProfileStatuses.Idle,
    CandidatesConst.ProfileStatuses.Waitlist,
    CandidatesConst.ProfileStatuses.PendingActive,
    CandidatesConst.ProfileStatuses.Active,
    CandidatesConst.ProfileStatuses.PendingBeach,
    CandidatesConst.ProfileStatuses.Beach,
    CandidatesConst.ProfileStatuses.PendingAlum,
    CandidatesConst.ProfileStatuses.PendingAlumDNR,
  ].map(profileStatus => ({
    label: profileStatus,
    value: profileStatus,
  })),
];

export const hasProfileStatusValue = (
  selectedStatuses: ProfileStatus[] = [],
  valueToCheck?: ProfileStatusValue
): boolean => {
  if (!valueToCheck) {
    return false;
  }
  return Array.isArray(valueToCheck)
    ? valueToCheck.filter(status => !selectedStatuses.includes(status))
        .length === 0
    : selectedStatuses.includes(valueToCheck);
};

export const flaggedTalentDisplayName = () => (
  <Grid>
    <GridRow name="FLAGGED_TALENT_ROW" columns={2} verticalAlign="middle">
      <GridColumn name="FLAGGED_TALENT_LABEL">Flagged</GridColumn>
      <GridColumn name="FLAGGED_TALENT_TOOLTIP_COLUMN">
        <Tooltip name="FLAGGED_TALENT_TOOLTIP">
          Please reach out to the Talent Owner before proceeding.
        </Tooltip>
      </GridColumn>
    </GridRow>
  </Grid>
);

export const isPriorityTalentDisplayName = () => (
  <Grid>
    <GridRow name="PRIORITY_TALENT_ROW" columns={2} verticalAlign="middle">
      <GridColumn name="PRIORITY_TALENT_LABEL">
        {UserBenchTalentSort[UserBenchTalentSortKeys.priority]}
      </GridColumn>
      <GridColumn name="PRIORITY_TALENT_TOOLTIP_COLUMN">
        <Tooltip name="PRIORITY_TALENT_TOOLTIP">
          Priority Talent have been on the Beach for over 6 weeks (2 weeks for
          Asia) or on the Waitlist for over 12 weeks (8 weeks for UK, 4 weeks
          for Asia) and are available for opportunities. They can be priced at
          lower margins to give cost savings to the client.
        </Tooltip>
      </GridColumn>
    </GridRow>
  </Grid>
);

export const buildCandidateIdsArray = (
  checkedRows: Array<Record<string, unknown>>
): string[] => {
  return checkedRows.reduce<string[]>((acc, row) => {
    if (row.id && typeof row.id === 'string') {
      acc.push(row.id);
    }
    return acc;
  }, []);
};

type ProfileStatusFilterRenderProps = {
  filters: { profileStatus?: ProfileStatus[] | null };
  onFilterChanges: (filters: { profileStatus: ProfileStatus[] }) => void;
};

export const profileStatusFiltersRender = ({
  filters,
  onFilterChanges,
}: ProfileStatusFilterRenderProps) => {
  return (
    <>
      {UserTalentFilterByProfileStatusList.map(choice => {
        const currentFilters = new Set(filters.profileStatus || []);
        const valueToAdd: readonly ProfileStatus[] = Array.isArray(choice.value)
          ? choice.value
          : [choice.value];

        const isSelected = hasProfileStatusValue(
          [...currentFilters],
          valueToAdd
        );

        const handleClick = () => {
          if (isSelected) {
            valueToAdd.forEach(status => currentFilters.delete(status));
          } else {
            valueToAdd.forEach(status => currentFilters.add(status));
          }

          onFilterChanges({
            profileStatus: [...currentFilters],
          });
        };

        return (
          <ContextMenuItem
            key={choice.label}
            name={`STATUS_FILTER_${choice.label.toUpperCase().replaceAll(/\s+/g, '_')}`}
            onClick={handleClick}
            selected={isSelected}
          >
            {choice.label}
          </ContextMenuItem>
        );
      })}
    </>
  );
};

type TalentOwnershipFilterRenderProps = {
  filters: { type?: keyof typeof UserBenchTalentTypes | null };
  onFilterChanges: (filters: {
    type: keyof typeof UserBenchTalentTypes;
  }) => void;
};

export const talentOwnershipFiltersRender = ({
  filters,
  onFilterChanges,
}: TalentOwnershipFilterRenderProps) => {
  return (
    <>
      {(
        Object.keys(UserBenchTalentTypes) as Array<
          keyof typeof UserBenchTalentTypes
        >
      ).map(ownerType => (
        <ContextMenuItem
          key={ownerType}
          name={`OWNERSHIP_FILTER_${ownerType.toUpperCase()}`}
          onClick={() => {
            onFilterChanges({
              type: ownerType,
            });
          }}
          selected={filters.type === ownerType}
        >
          {UserBenchTalentTypes[ownerType]}
        </ContextMenuItem>
      ))}
    </>
  );
};
